import { Component, OnInit } from "@angular/core";
import { ColumnMapping, DataFilter } from "../../core/components/dynamic-table/dynamic-table.component";
import { CardItemMapping } from "@core/components/dynamic-cards/dynamic-cards.component";
import { CompanyV2Service } from "@core/services/company-v2.service";
import { HorizontalBarType } from "../../core/components/generic-listing/generic-listing.component";
import { isSafari } from "../../utils/utils";
import { DeviceDetectorService } from "ngx-device-detector";
import { UsersService } from "src/app/services/users/users.service";
import { Router } from "@angular/router";
import { AdminPanelService } from "src/app/services/admin/admin-panel.service";
import { Subscription, takeUntil } from "rxjs";
import { TabUtilsService } from "src/app/services/tab-utils.service";
import { ModalUtilsService } from "src/app/services/modal-utils.service";
import { UserGroupsService } from "src/app/services/user-groups/user-groups.service";
import { UserRolesService } from "src/app/services/user-roles/user-roles.service";
import { RouterService } from "src/app/services/router/router.service";
import { InvestorTab } from "@core/models/types";
import { MessageService } from "primeng/api";

@Component({
  selector: "bpc-admin-panel",
  templateUrl: "./admin-panel.component.html",
  styleUrl: "./admin-panel.component.scss",
})
export class AdminPanelComponent implements OnInit {
  type : string | null = "";
  isLoading = false;

  users: CardItemMapping[] | any = [];
  dataColumns!: ColumnMapping[];

  usersColumns: ColumnMapping[] = [
    { header: "Picture", showHeader: false, mappedTo: "profilePictureUrl", openLink: "picture"},
    { header: "Name", showHeader: true, mappedTo: "name" },
    { header: "Email", showHeader: true, mappedTo: "email" },
    { header: "DealCloud ID", showHeader: true, mappedTo: "externalId" },
    { header: "Role(s)	", showHeader: true, mappedTo: "userRolesList" },
    { header: "Pod(s)", showHeader: true, mappedTo: "userPodsList" },
    { header: "Group(s)", showHeader: true, mappedTo: "userGroupsList", openLink: "editUser" },
  ];
  alertsColumns: ColumnMapping[] = [
    { header: "Cron Job Name", showHeader: false, mappedTo: "id", openLink: "updateCronJob"},
    { header: "Frequency", showHeader: true, mappedTo: "frequency" },
    { header: "Recipients", showHeader: true, mappedTo: "reciepientsList" },
    { header: "Last Execution", showHeader: true, mappedTo: "lastExecutionDate", openLink: "deleteCronJob" },
  ];

  usersFilters: DataFilter = {
    showAllUsers: "false"
  };

  totalCount = 0;

  childTabs: any = [
    { label: "Users", route: "" },
    { label: "Alerts", route: "alert" },
  ];

  get isMobile(): boolean {
    return this.deviceDetectorService.isMobile();
  }

  constructor(
    private usersService: UsersService,
    private deviceDetectorService: DeviceDetectorService,
    private companyV2Service: CompanyV2Service,
    private adminPanelService: AdminPanelService,
    private tabUtilsService: TabUtilsService,
    private modalUtilsService: ModalUtilsService,
    private userGroupsService: UserGroupsService,
    private userRolesService: UserRolesService,
    private routerService: RouterService,
    private messageService: MessageService,
  ) {}

  private refreshDataSubscription!: Subscription;

  protected showErrorToastMessage(message: string) {
    this.messageService.add({ key: "home-bc", severity: "error", detail: message, life: 10_000 });
  }
  protected showSuccessToastMessage(message: string) {
    this.messageService.add({ key: "home-bc", severity: "success", detail: message, life: 5_000 });
  }

  ngOnInit(): void {
    this.routerService.updateCurrentTab(InvestorTab.ADMIN_PANEL);
    const url = this.routerService.url;

    if (url) {
      this.type = url.substring(url.lastIndexOf('/') + 1) ?? "";
    }

    this.refreshDataSubscription = this.tabUtilsService.refreshData.subscribe(() => {
      this.loadData();
    });
    this.loadData();
  }

  loadData(){
    switch(this.type){
      case "alert":
        this.dataColumns = this.alertsColumns;
        this.fetchAlerts();
      break;
      default:
        this.dataColumns = this.usersColumns;
        this.fetchUsers();
        this.type = "";
      break;
    }
  }

  fetchUsers() {
    if (this.isLoading) {
      return; 
    }

    this.isLoading = true;

    this.usersService.getUsersV2(this.usersFilters)
      .pipe(takeUntil(this.routerService.destroy$))
      .subscribe({
      next: (response) => {
        if (response?.data) {
          this.users = this.companyV2Service.transformUsersData(response.data);
          
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.users = [];
        this.showErrorToastMessage("Failed to load data, please refresh!");
      },
      complete: () => {
      },
    });
  }

  fetchAlerts() {
    if (this.isLoading) {
      return; 
    }

    this.isLoading = true;

    this.adminPanelService.getAllJobs()
      .pipe(takeUntil(this.routerService.destroy$))
      .subscribe({
      next: (response) => {
        if (response?.data?.data) {
          this.users = this.companyV2Service.transformAlertsData(response.data.data);
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        
      },
      complete: () => {
      },
    });
  }

  addOrEditUser(user: any = null) {
    let modalParameters = this.modalUtilsService.modalParameters;
    if (user) {
      modalParameters.currentModalAction = "EditUser";
      modalParameters.name = user.name;
      modalParameters.initials = user.initials;
      modalParameters.email = user.email;
      modalParameters.externalId = user.externalId;
      modalParameters.base64Image = user.profilePictureUrl ?? "";
      const userGroupIds = user.userGroups.map((group: any) => {
        return group.groupId;
      });
      const userRoleIds = user.userRoles.map((role: any) => {
        return role.roleId;
      });
      const userPodsIds = user.userGroups.map((group: any) => {
        return group.groupId;
      });
      const selectedGroups = this.userGroupsService.userGroups.filter((group) => {
        return userGroupIds.includes(group.id);
      });
      modalParameters.externalId = user.externalId;
      modalParameters.userGroups = selectedGroups;
      const selectedRoles = this.userRolesService.userRoles.filter((role) => {
        return userRoleIds.includes(role.roleId);
      });
      modalParameters.userRoles = selectedRoles;
      modalParameters.userId = user.id;
      modalParameters.isActive = user.isActive;
    } else {
      modalParameters.currentModalAction = "AddUser";
    }

    this.modalUtilsService.toggalModal.next(modalParameters);
  }

  get showAllUsers():boolean{
    return this.usersFilters.showAllUsers === "true";
  }

  set showAllUsers(value: boolean) {
    value ?  this.usersFilters.showAllUsers = "true" : this.usersFilters.showAllUsers = "false";

    this.loadData();
  }

  onFilterChange(filters: any) {
    this.users = [];
    this.usersFilters = { ...this.usersFilters, ...filters };
    this.loadData();
  }

  ngOnDestroy(): void {
    if (this.refreshDataSubscription) {
      this.refreshDataSubscription.unsubscribe();
    }
  }
}
